import React, { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Phone, MapPin, ShieldCheck, CheckCircle2,
  Calendar, User, Mail, MessageSquare,
} from "lucide-react";
import { PROPERTY_BY_SLUG, statusBadge } from "../data/properties";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useSEO } from "../lib/seo";
import { trackFormSubmit, trackPhoneClick } from "../lib/tracking";

const ROLE_OPTIONS = [
  ["participant", "I'm the participant"],
  ["family", "Family member or carer"],
  ["coordinator", "Support Coordinator / LAC"],
  ["clinician", "Clinician / Hospital"],
  ["other", "Other"],
];

const TIMEFRAMES = [
  ["asap", "As soon as possible"],
  ["1-month", "Within 1 month"],
  ["3-months", "Within 3 months"],
  ["exploring", "Just exploring"],
];

export default function PropertyEnquirePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const p = PROPERTY_BY_SLUG[slug];

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    role: "participant",
    timeframe: "asap",
    message: "",
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useSEO({
    title: p ? `Enquire · ${p.name} · ${p.suburb}, ${p.state} | Alpha Community Care` : "Enquire | Alpha",
    description: p ? `Enquire about ${p.name} — a real Alpha SIL home in ${p.suburb}, ${p.state}. 1:1 and 1:2 support only. Reply within one business day.` : undefined,
    canonical: p ? `https://alphacommunitycare.com.au/homes/${p.slug}/enquire` : undefined,
  });

  if (!p) return <Navigate to="/vacancies" replace />;

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.phone) {
      setErr("Please share your name, email and phone so we can reply.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `[Property enquiry · ${p.name} · ${p.suburb} ${p.state}]\nRole: ${form.role}\nTimeframe: ${form.timeframe}\n\n${form.message}`.trim(),
        type: "vacancy_enquiry",
      });
      trackFormSubmit("property_enquiry", { property: p.slug, role: form.role, timeframe: form.timeframe });
      setDone(true);
    } catch (e) {
      setErr("Sorry — we couldn't send that just now. Please try again or call 1300 380 692.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[80vh] alpha-gradient-soft-band flex items-center" data-testid="property-enquire-done">
        <div className="alpha-container py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="alpha-overline">Enquiry received</div>
            <h1 className="mt-3 font-display text-3xl sm:text-[44px] font-medium tracking-tight leading-[1.05]">
              Thanks — we'll be in touch within <span className="alpha-serif-italic alpha-gradient-text">one business day</span>.
            </h1>
            <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px] max-w-xl">
              An Alpha intake team member will personally review your enquiry about <strong>{p.name}</strong> and reply with availability, suitability notes and next steps.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              <Link to={p.href} className="alpha-btn-ghost text-sm !py-2.5 !px-5"><ArrowLeft className="w-4 h-4" /> Back to {p.name}</Link>
              <Link to="/vacancies" className="alpha-btn-primary text-sm !py-2.5 !px-5">Browse all homes <ArrowRight className="w-4 h-4" /></Link>
              <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `enquire-${p.slug}-done`)} className="alpha-btn-green text-sm !py-2.5 !px-5"><Phone className="w-4 h-4" /> 1300 380 692</a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="alpha-card overflow-hidden !rounded-[20px]">
              <img src={p.images[0].src} alt={p.images[0].alt} className="aspect-[4/3] object-cover w-full" />
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--alpha-mute)] font-semibold inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.suburb}, {p.state}</div>
                <div className="font-display font-semibold text-lg mt-1">{p.name}</div>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1.5">{p.oneLiner}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sb = statusBadge(p);

  return (
    <div className="bg-white" data-testid={`property-enquire-${p.slug}`}>
      <div className="alpha-container py-8 md:py-12 grid lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Left — form */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Link to={p.href} className="text-xs uppercase tracking-[0.22em] text-[color:var(--alpha-mute)] font-semibold inline-flex items-center gap-1.5 hover:text-[color:var(--alpha-purple-700)]">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {p.name}
          </Link>
          <div className="alpha-overline mt-5">Enquire about this home</div>
          <h1 className="mt-2 font-display font-medium tracking-tight leading-[1.05]" style={{ fontSize: "clamp(28px, 3.6vw, 44px)" }}>
            Tell us a little about the participant.
          </h1>
          <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px] max-w-xl">
            We respond personally within one business day with availability, suitability and next steps for <strong>{p.name}</strong>.
          </p>

          <form onSubmit={submit} className="mt-7 grid gap-4" data-testid="property-enquire-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name" Icon={User} required>
                <Input value={form.name} onChange={update("name")} placeholder="Full name" data-testid="enquire-name" />
              </Field>
              <Field label="I am a / I am the…" Icon={User} required>
                <Select value={form.role} onValueChange={update("role")}>
                  <SelectTrigger className="h-11" data-testid="enquire-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" Icon={Mail} required>
                <Input type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" data-testid="enquire-email" />
              </Field>
              <Field label="Phone" Icon={Phone} required>
                <Input type="tel" value={form.phone} onChange={update("phone")} placeholder="04xx xxx xxx" data-testid="enquire-phone" />
              </Field>
            </div>
            <Field label="Timeframe" Icon={Calendar}>
              <Select value={form.timeframe} onValueChange={update("timeframe")}>
                <SelectTrigger className="h-11" data-testid="enquire-timeframe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEFRAMES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Anything else we should know?" Icon={MessageSquare}>
              <Textarea
                value={form.message}
                onChange={update("message")}
                rows={4}
                placeholder="Goals, support needs, NDIS plan status, suitability questions — share what's helpful."
                data-testid="enquire-message"
              />
            </Field>

            {err && <div className="text-sm text-red-600" data-testid="enquire-error">{err}</div>}

            <div className="mt-2 flex flex-wrap gap-2.5">
              <button
                type="submit"
                className="alpha-btn-primary !py-3 !px-6 disabled:opacity-60"
                disabled={submitting}
                data-testid="enquire-submit"
              >
                {submitting ? "Sending…" : "Send enquiry"} <ArrowRight className="w-4 h-4" />
              </button>
              <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `enquire-${p.slug}`)} className="alpha-btn-green !py-3 !px-6">
                <Phone className="w-4 h-4" /> 1300 380 692
              </a>
            </div>

            <div className="mt-2 flex items-center gap-2 text-[12px] text-[color:var(--alpha-mute)]">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-purple-600)]" /> Private &amp; confidential. Your details only go to Alpha intake.
            </div>
          </form>
        </div>

        {/* Right — property summary */}
        <aside className="lg:col-span-5 order-1 lg:order-2">
          <div className="lg:sticky lg:top-24">
            <div className="alpha-card overflow-hidden !rounded-[22px]">
              <div className="relative aspect-[4/3]">
                <img src={p.images[0].src} alt={p.images[0].alt} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0625]/55 to-transparent" />
                <span className={`absolute top-3 left-3 alpha-chip !py-1 !px-2.5 !text-[10.5px] font-bold uppercase tracking-[0.18em] ${
                  sb.tone === "green" ? "!bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)]"
                  : sb.tone === "purple" ? "!bg-[color:var(--alpha-purple-700)] !text-white !border-[color:var(--alpha-purple-800)]"
                  : "!bg-white !text-[color:var(--alpha-ink)]"
                }`}>{sb.label}</span>
                <span className="absolute top-3 right-3 alpha-chip !bg-white !text-[color:var(--alpha-purple-700)] !border-white !py-1 !px-2.5 !text-[11px] font-bold">1:1 & 1:2 only</span>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--alpha-mute)] font-semibold inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.suburb}, {p.state} · SIL</div>
                <h2 className="font-display font-semibold text-xl mt-1">{p.name}</h2>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1.5">{p.oneLiner}</p>
                <ul className="mt-4 space-y-2 text-[13px]">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[color:var(--alpha-green-600)]" /><span><strong>1:1 and 1:2 support only</strong></span></li>
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[color:var(--alpha-purple-600)]" /><span>Registered NDIS · Practice Standards</span></li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[color:var(--alpha-green-600)]" /><span>24/7 on-call support team</span></li>
                </ul>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, Icon, required, children }) {
  return (
    <label className="block">
      <span className="alpha-overline mb-1.5 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3 text-[color:var(--alpha-purple-600)]" />}
        {label}{required && <span className="text-[color:var(--alpha-purple-600)]">*</span>}
      </span>
      {children}
    </label>
  );
}
